import type { Account, ProductType } from '@shared/products-types';
import { logger } from '@luna/logger';
import { deepClone } from '@luna/utils';

import { createGAPIAccount, deleteGAPIAccount, selectGAPIAccounts } from './gAPIAccountEntitySlice';

type ExternalAccountBase = { id: string; alias?: string; type: string; productSubType?: string };

type ResultHandlers<ExternalAccount> = {
    onSaveSuccess?: (gapiAccount: Account, externalAccount: ExternalAccount) => void;
    onDeleteSuccess?: (gapiAccountId: string) => void;
    onExistingUpdate?: (gapiAccount: Account, externalAccount: ExternalAccount) => void;
};

const createGAPIAccountMetaDataCache: Record<string, Record<string, unknown>> = {};
const setCreateGAPIAccountIsPending = (accountId: string, value: boolean): void => {
    createGAPIAccountMetaDataCache[accountId] = {
        ...createGAPIAccountMetaDataCache[accountId],
        isPending: value,
    };
};
const setCreateGAPIAccountCreated = (accountId: string): void => {
    createGAPIAccountMetaDataCache[accountId] = {
        ...createGAPIAccountMetaDataCache[accountId],
        createdInThisSession: true,
    };
};

const deleteGAPIAccountMetaDataCache: Record<string, Record<string, unknown>> = {};

const setDeleteGAPIAccountIsPending = (accountId: string, value: boolean): void => {
    deleteGAPIAccountMetaDataCache[accountId] = {
        ...deleteGAPIAccountMetaDataCache[accountId],
        isPending: value,
    };
};
const setDeleteGAPIAccountDeleted = (accountId: string): void => {
    deleteGAPIAccountMetaDataCache[accountId] = {
        ...deleteGAPIAccountMetaDataCache[accountId],
        deletedInThisSession: true,
    };
};
const createExternalAccountOnGapi = <ExternalAccount extends ExternalAccountBase>(
    externalAccount: ExternalAccount,
    externalAccountType: ProductType,
    onSaveSuccess?: (gapiAccount: Account, externalAccount: ExternalAccount) => void,
) => {
    if (
        createGAPIAccountMetaDataCache[externalAccount.id]?.isPending ||
        createGAPIAccountMetaDataCache[externalAccount.id]?.createdInThisSession
    ) {
        return;
    }

    const newGapiAccountPayload = {
        id: externalAccount.id,
        alias: externalAccount.alias,
        productType: externalAccountType,
        productSubType: externalAccount.type,
        productId: externalAccount.id,
        additionalData: {
            hidden: false,
        },
    };

    setCreateGAPIAccountIsPending(newGapiAccountPayload.id, true);

    // @ts-expect-error the required fields are filled on BE after creation
    createGAPIAccount(newGapiAccountPayload)
        .then((response) => {
            onSaveSuccess?.(deepClone(response), deepClone(externalAccount));
            setCreateGAPIAccountIsPending(newGapiAccountPayload.id, false);
            setCreateGAPIAccountCreated(newGapiAccountPayload.id); // avoid creating multiple GAPI accounts of the same id in the same session.
        })
        .catch((e) => {
            setCreateGAPIAccountIsPending(newGapiAccountPayload.id, false);
            logger.error('Error on GAPI account creation sync: ', e);
        });
};

export const processExternalAccounts = <ExternalAccount extends ExternalAccountBase>(
    externalAccounts: ExternalAccount[],
    externalAccountType: ProductType,
    resultHandlers?: ResultHandlers<ExternalAccount>,
) => {
    const gapiAccounts = selectGAPIAccounts();

    const externalAccountsMap = externalAccounts.reduce(
        function (map, externalAccount) {
            map[externalAccount.id] = externalAccount;
            return map;
        },
        {} as Record<string, ExternalAccount>,
    );

    const gapiAccountsMap = gapiAccounts
        .filter(({ productType }) => productType === externalAccountType)
        .reduce(
            function (map, account) {
                map[account.productId || account.id] = account;
                return map;
            },
            {} as Record<string, Account>,
        );

    Object.keys(externalAccountsMap).forEach((externalAccountId) => {
        const gapiAccount = gapiAccountsMap[externalAccountId];

        if (gapiAccount) {
            delete gapiAccountsMap[externalAccountId];
            resultHandlers?.onExistingUpdate?.(gapiAccount, externalAccountsMap[externalAccountId]);
            return;
        }

        createExternalAccountOnGapi(
            externalAccountsMap[externalAccountId],
            externalAccountType,
            resultHandlers?.onSaveSuccess,
        );
    });

    Object.values(gapiAccountsMap).forEach(({ id }) => {
        if (
            deleteGAPIAccountMetaDataCache[id]?.isPending ||
            deleteGAPIAccountMetaDataCache[id]?.deletedInThisSession
        ) {
            return;
        }
        setDeleteGAPIAccountIsPending(id, true);

        deleteGAPIAccount({ id })
            .then(() => {
                setDeleteGAPIAccountDeleted(id);
                resultHandlers?.onDeleteSuccess?.(id);
            })
            .finally(() => setDeleteGAPIAccountIsPending(id, false));
    });
};
